import { Trans } from "@lingui/macro";
import {
	ActionIcon,
	Badge,
	Button,
	Container,
	Grid,
	Group,
	Image,
	Modal,
	Paper,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useNavigate } from "@remix-run/react";
import {
	ArrowLeft,
	ArrowRight,
	Building2,
	Calendar,
	Stethoscope,
	User,
	UserPlus,
} from "lucide-react";
import { useState } from "react";
import { useCompanyConfig } from "~/hooks/useCompanyConfig";
import { usePortalState } from "~/utils/states";
import { PortalPinReminderBanner } from "./portal-pin-reminder-banner";

interface ActiveBooking {
	booking_id: string;
	booking_kode: string;
	booking_nama: string;
	booking_tanggal: string;
	booking_unit_nama: string;
	booking_dokter_nama: string;
	booking_bayar_nama: string;
	booking_noantrian: number;
	booking_noantrian_jam_awal: string;
	booking_noantrian_jam_akhir: string;
	jam_layanan_awal?: string;
	jam_layanan_akhir?: string;
	tenant_id: string;
	tenant_name: string;
	payment_link?: string;
	antrean_saat_ini: number;
}

interface PortalHomepageProps {
	activeBookings?: ActiveBooking[];
}

export default function PortalHomepage({
	activeBookings = [],
}: PortalHomepageProps) {
	const navigate = useNavigate();
	const { portalPatient, selectedTenant, isAuthenticated, setPortalPatient } =
		usePortalState();
	const { companyConfig } = useCompanyConfig();
	const [currentBookingIndex, setCurrentBookingIndex] = useState(0);
	const [mcuModalOpened, setMcuModalOpened] = useState(false);
	const [qrModalOpened, setQrModalOpened] = useState(false);

	// Debug log
	console.log("Portal Patient State:", {
		isAuthenticated,
		has_pin: portalPatient?.has_pin,
		global_patient_id: portalPatient?.global_patient_id,
	});

	const hasActiveBookings = activeBookings && activeBookings.length > 0;
	const currentBooking = hasActiveBookings
		? activeBookings[currentBookingIndex]
		: null;

	const handlePreviousBooking = () => {
		if (currentBookingIndex > 0) {
			setCurrentBookingIndex(currentBookingIndex - 1);
		}
	};

	const handleNextBooking = () => {
		if (currentBookingIndex < activeBookings.length - 1) {
			setCurrentBookingIndex(currentBookingIndex + 1);
		}
	};

	// Format date helper
	const formatDate = (dateStr: string) => {
		const date = new Date(dateStr);
		const months = [
			"Jan",
			"Feb",
			"Mar",
			"Apr",
			"Mei",
			"Jun",
			"Jul",
			"Agu",
			"Sep",
			"Okt",
			"Nov",
			"Des",
		];
		return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
	};

	return (
		<div style={{ minHeight: "100vh", background: "#f9fafb" }}>
			{/* PIN Reminder Banner - Show if user is authenticated but hasn't set PIN */}
			{isAuthenticated &&
				portalPatient &&
				(portalPatient.has_pin === false ||
					portalPatient.has_pin === undefined) && <PortalPinReminderBanner />}

			<Container size="xl" py="xl">
				{/* Hero Section */}
				<div
					style={{
						backgroundImage: "url(/default-portal-banner.webp)",
						backgroundSize: "cover",
						backgroundPosition: "center",
						borderRadius: "24px",
						padding: "3rem 2rem",
						marginBottom: "2rem",
						position: "relative",
						overflow: "hidden",
					}}
				>
					{/* Overlay for better text readability */}
					<div
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: "rgba(255, 255, 255, 0.7)",
							borderRadius: "24px",
						}}
					/>

					{/* Content */}
					<div style={{ position: "relative", zIndex: 1 }}>
						<Title order={1} size="h1" fw={700} c="gray.9" ta="center" mb="sm">
							<Trans>Selamat Datang di Portal Booking</Trans>{" "}
							{companyConfig.name}
						</Title>
						<Text size="lg" c="gray.7" ta="center">
							<Trans>
								Solusi digital layanan kesehatan anda, pilih Pendaftaran Janji
								Temu atau Pendaftaran MCU
							</Trans>
						</Text>
					</div>
				</div>

				<Grid gutter="lg">
					{/* Sidebar - Always show */}
					<Grid.Col span={{ base: 12, md: 4, lg: 4 }}>
						<Paper
							radius="20px"
							withBorder
							shadow="sm"
							style={{
								position: "sticky",
								top: "1rem",
								background: "white",
								border: "1px solid #e5e7eb",
								overflow: "hidden",
								paddingTop: "1rem",
								paddingBottom: "1rem",
								paddingLeft: "1.25rem",
								paddingRight: "1.25rem",
							}}
						>
							<Stack gap="xs">
								{/* Header with navigation */}
								<Group justify="space-between" align="center" mb={-4}>
									<Text size="md" fw={600} c="gray.7">
										<Trans>Detil Booking</Trans>
									</Text>
									{activeBookings.length > 1 ? (
										<Group gap={6}>
											<ActionIcon
												variant="subtle"
												size="sm"
												onClick={handlePreviousBooking}
												disabled={currentBookingIndex === 0}
											>
												<ArrowLeft size={16} />
											</ActionIcon>
											<Text size="sm" c="gray.6">
												{currentBookingIndex + 1}/{activeBookings.length}{" "}
												<Trans>Booking</Trans>
											</Text>
											<ActionIcon
												variant="subtle"
												size="sm"
												onClick={handleNextBooking}
												disabled={
													currentBookingIndex === activeBookings.length - 1
												}
											>
												<ArrowRight size={16} />
											</ActionIcon>
										</Group>
									) : (
										<Text size="sm" c="gray.6">
											0/0 <Trans>Booking</Trans>
										</Text>
									)}
								</Group>

								{hasActiveBookings && currentBooking ? (
									<>
										{/* Patient Info */}
										<div
											style={{
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												marginTop: "-0.25rem",
											}}
										>
											<div
												style={{
													width: "70px",
													height: "70px",
													borderRadius: "50%",
													background:
														"linear-gradient(135deg, #3b82f6, #1d4ed8)",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													color: "white",
													fontSize: "28px",
													fontWeight: "600",
													marginBottom: "0.4rem",
													boxShadow: "0 4px 12px rgba(59, 130, 246, 0.25)",
												}}
											>
												{currentBooking.booking_nama?.charAt(0)?.toUpperCase()}
											</div>
											<Text size="md" fw={600} ta="center" tt="uppercase">
												{currentBooking.booking_nama}
											</Text>
										</div>

										{/* QR Code & Queue Badges - Side by Side */}
										<Group gap="xs" align="flex-start" wrap="nowrap">
											{/* QR Code Left */}
											<Stack gap={6} style={{ flex: "0 0 auto" }}>
												<div
													style={{
														padding: "0.5rem",
														background: "white",
														borderRadius: "12px",
														border: "2px solid #e5e7eb",
													}}
												>
													<Image
														src={`https://quickchart.io/qr?text=${currentBooking.booking_kode}&size=200`}
														alt="QR Code"
														w={120}
														h={120}
													/>
												</div>
												<Button
													variant="outline"
													size="xs"
													fullWidth
													onClick={() => setQrModalOpened(true)}
													style={{
														borderRadius: "8px",
														fontSize: "12px",
														padding: "0.4rem 0.6rem",
														height: "auto",
													}}
												>
													<Trans>Perbesar QR</Trans>
												</Button>
											</Stack>

											{/* Queue Badges & Booking Code Right */}
											<Stack gap={6} style={{ flex: 1 }}>
												{/* Red Badge - Antrean Saat Ini */}
												<div
													style={{
														display: "flex",
														alignItems: "stretch",
														borderRadius: "12px",
														border: "2px solid #DC2626",
														boxShadow: "0 2px 8px rgba(239, 68, 68, 0.2)",
														overflow: "hidden",
														maxWidth: "220px",
													}}
												>
													<div
														style={{
															flex: 1,
															background: "white",
															padding: "0.5rem 0.6rem",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															borderTopRightRadius: "10px",
															borderBottomRightRadius: "10px",
														}}
													>
														<Text
															size="13px"
															fw={500}
															c="gray.8"
															ta="center"
															style={{ lineHeight: 1.3 }}
														>
															Antrean
															<br />
															Saat Ini
														</Text>
													</div>
													<div
														style={{
															background:
																"linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
															padding: "0.5rem 0.85rem",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															minWidth: "60px",
														}}
													>
														<Text size="28px" fw={700} c="white">
															{currentBooking.antrean_saat_ini || 0}
														</Text>
													</div>
												</div>

												{/* Blue Badge - Antrean Anda */}
												<div
													style={{
														display: "flex",
														alignItems: "stretch",
														borderRadius: "12px",
														border: "2px solid #1D4ED8",
														boxShadow: "0 2px 8px rgba(59, 130, 246, 0.2)",
														overflow: "hidden",
														maxWidth: "220px",
													}}
												>
													<div
														style={{
															flex: 1,
															background: "white",
															padding: "0.5rem 0.6rem",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															borderTopRightRadius: "10px",
															borderBottomRightRadius: "10px",
														}}
													>
														<Text
															size="13px"
															fw={500}
															c="gray.8"
															ta="center"
															style={{ lineHeight: 1.3 }}
														>
															Antrean
															<br />
															Anda
														</Text>
													</div>
													<div
														style={{
															background:
																"linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
															padding: "0.5rem 0.85rem",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															minWidth: "60px",
														}}
													>
														<Text size="28px" fw={700} c="white">
															{String(
																currentBooking.booking_noantrian,
															).padStart(2, "0")}
														</Text>
													</div>
												</div>

												{/* Booking Code */}
												<div
													style={{ textAlign: "center", marginTop: "0.25rem" }}
												>
													<Text size="xs" c="gray.6" mb={3}>
														<Trans>Kode Booking</Trans>
													</Text>
													<Text
														size="md"
														fw={700}
														style={{ letterSpacing: "0.5px" }}
													>
														{currentBooking.booking_kode}
													</Text>
												</div>
											</Stack>
										</Group>

										{/* Divider */}
										<div
											style={{
												height: "1px",
												background: "#e5e7eb",
												margin: "0.4rem 0 0.2rem 0",
											}}
										/>

										{/* Booking Details - Figma Style with Icons */}
										<Stack gap={6}>
											<Group gap={8} wrap="nowrap">
												<div
													style={{
														minWidth: "20px",
														height: "20px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}
												>
													<Building2 size={16} color="#6b7280" />
												</div>
												<Text size="sm" c="gray.8" style={{ flex: 1 }}>
													{currentBooking.tenant_name}
												</Text>
											</Group>
											<Group gap={8} wrap="nowrap">
												<div
													style={{
														minWidth: "20px",
														height: "20px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}
												>
													<Stethoscope size={16} color="#6b7280" />
												</div>
												<Text size="sm" c="gray.8" style={{ flex: 1 }}>
													{currentBooking.booking_unit_nama}
												</Text>
											</Group>
											<Group gap={8} wrap="nowrap">
												<div
													style={{
														minWidth: "20px",
														height: "20px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}
												>
													<User size={16} color="#6b7280" />
												</div>
												<Text size="sm" c="gray.8" style={{ flex: 1 }}>
													{currentBooking.booking_dokter_nama}
												</Text>
											</Group>
											<Group gap={8} wrap="nowrap">
												<div
													style={{
														minWidth: "20px",
														height: "20px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}
												>
													<Calendar size={16} color="#6b7280" />
												</div>
												<Text size="sm" c="gray.8" style={{ flex: 1 }}>
													{formatDate(currentBooking.booking_tanggal)}{" "}
													{currentBooking.booking_noantrian_jam_awal?.substring(
														0,
														5,
													)}
													-
													{currentBooking.booking_noantrian_jam_akhir?.substring(
														0,
														5,
													)}
												</Text>
											</Group>
										</Stack>
									</>
								) : (
									<>
										{/* Empty State */}
										<Stack align="center" gap="md" py="xl">
											<div
												style={{
													width: "100px",
													height: "100px",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}}
											>
												<svg
													width="100"
													height="100"
													viewBox="0 0 100 100"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<rect
														x="20"
														y="15"
														width="50"
														height="60"
														rx="4"
														fill="#6B7280"
													/>
													<rect
														x="30"
														y="25"
														width="30"
														height="4"
														rx="2"
														fill="white"
													/>
													<rect
														x="30"
														y="35"
														width="30"
														height="4"
														rx="2"
														fill="white"
													/>
													<rect
														x="30"
														y="45"
														width="20"
														height="4"
														rx="2"
														fill="white"
													/>
													<circle cx="70" cy="65" r="18" fill="#9CA3AF" />
													<circle cx="70" cy="65" r="12" fill="white" />
													<path
														d="M70 59 L70 71 M64 65 L76 65"
														stroke="#9CA3AF"
														strokeWidth="2"
														strokeLinecap="round"
													/>
												</svg>
											</div>
											<Text size="sm" fw={600} c="gray.8" ta="center">
												<Trans>Tidak Memiliki Booking</Trans>
											</Text>
											<Text size="xs" c="gray.5" ta="center" px="md">
												<Trans>Lakukan pendaftaran Janji Temu atau MCU</Trans>
											</Text>
										</Stack>
									</>
								)}
							</Stack>
						</Paper>
					</Grid.Col>

					{/* Pendaftaran Janji Temu Card */}
					<Grid.Col span={{ base: 12, md: 4, lg: 4 }}>
						<div
							style={{
								cursor: "pointer",
								borderRadius: "20px",
								overflow: "hidden",
								background: "linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)",
								boxShadow: "0 10px 30px rgba(59, 130, 246, 0.3)",
								transform: "translateY(0) scale(1)",
								transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
								position: "relative",
								minHeight: "180px",
							}}
							onClick={() => navigate("/portal-branches")}
							onMouseEnter={(e) => {
								e.currentTarget.style.transform =
									"translateY(-8px) scale(1.02)";
								e.currentTarget.style.boxShadow =
									"0 20px 40px rgba(59, 130, 246, 0.4)";
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.transform = "translateY(0) scale(1)";
								e.currentTarget.style.boxShadow =
									"0 10px 30px rgba(59, 130, 246, 0.3)";
							}}
						>
							{/* Decorative Circles */}
							<div
								style={{
									position: "absolute",
									top: "-20px",
									right: "-20px",
									width: "100px",
									height: "100px",
									borderRadius: "50%",
									background: "rgba(255, 255, 255, 0.1)",
								}}
							/>
							<div
								style={{
									position: "absolute",
									bottom: "-30px",
									left: "-30px",
									width: "80px",
									height: "80px",
									borderRadius: "50%",
									background: "rgba(255, 255, 255, 0.08)",
								}}
							/>

							<div
								style={{
									padding: "1.5rem",
									color: "white",
									textAlign: "center",
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									height: "100%",
									gap: "1rem",
									position: "relative",
									zIndex: 1,
								}}
							>
								{/* Icon */}
								<div
									style={{
										width: "60px",
										height: "60px",
										borderRadius: "50%",
										background: "rgba(255, 255, 255, 0.2)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										boxShadow: "0 8px 32px rgba(255, 255, 255, 0.2)",
									}}
								>
									<UserPlus size={28} color="white" strokeWidth={2} />
								</div>

								<div>
									{/* Title */}
									<Title
										order={3}
										size="h3"
										c="white"
										mb="xs"
										style={{ fontWeight: 600 }}
									>
										<Trans>REGISTRATION</Trans>
									</Title>

									{/* Description */}
									<Text
										size="sm"
										c="rgba(255, 255, 255, 0.9)"
										style={{ lineHeight: 1.6 }}
									>
										<Trans>
											Daftar dan buat janji untuk konsultasi kesehatan
										</Trans>
									</Text>
								</div>

								{/* Button */}
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
										padding: "12px 24px",
										borderRadius: "12px",
										background: "rgba(255, 255, 255, 0.2)",
										border: "1px solid rgba(255, 255, 255, 0.3)",
										backdropFilter: "blur(10px)",
										fontWeight: 500,
										fontSize: "14px",
									}}
								>
									<Trans>Mulai Sekarang</Trans>
									<span style={{ fontSize: "16px" }}>→</span>
								</div>
							</div>
						</div>
					</Grid.Col>

					{/* Pendaftaran MCU Card */}
					<Grid.Col span={{ base: 12, md: 4, lg: 4 }}>
						<div
							style={{
								cursor: "pointer",
								borderRadius: "20px",
								overflow: "hidden",
								background: "linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)",
								boxShadow: "0 10px 30px rgba(59, 130, 246, 0.3)",
								transform: "translateY(0) scale(1)",
								transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
								position: "relative",
								minHeight: "180px",
							}}
							onClick={() => navigate("/portal-pre-assessment-mcu")}
							onMouseEnter={(e) => {
								e.currentTarget.style.transform =
									"translateY(-8px) scale(1.02)";
								e.currentTarget.style.boxShadow =
									"0 20px 40px rgba(59, 130, 246, 0.4)";
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.transform = "translateY(0) scale(1)";
								e.currentTarget.style.boxShadow =
									"0 10px 30px rgba(59, 130, 246, 0.3)";
							}}
						>
							{/* Decorative Circles */}
							<div
								style={{
									position: "absolute",
									top: "-20px",
									right: "-20px",
									width: "100px",
									height: "100px",
									borderRadius: "50%",
									background: "rgba(255, 255, 255, 0.1)",
								}}
							/>
							<div
								style={{
									position: "absolute",
									bottom: "-30px",
									left: "-30px",
									width: "80px",
									height: "80px",
									borderRadius: "50%",
									background: "rgba(255, 255, 255, 0.08)",
								}}
							/>

							<div
								style={{
									padding: "1.5rem",
									color: "white",
									textAlign: "center",
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									height: "100%",
									gap: "1rem",
									position: "relative",
									zIndex: 1,
								}}
							>
								{/* Icon */}
								<div
									style={{
										width: "60px",
										height: "60px",
										borderRadius: "50%",
										background: "rgba(255, 255, 255, 0.2)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										boxShadow: "0 8px 32px rgba(255, 255, 255, 0.2)",
									}}
								>
									<Stethoscope size={28} color="white" strokeWidth={2} />
								</div>

								<div>
									{/* Title */}
									<Title
										order={3}
										size="h3"
										c="white"
										mb="xs"
										style={{ fontWeight: 600 }}
									>
										<Trans>MCU PRE-ASSESSMENT</Trans>
									</Title>

									{/* Description */}
									<Text
										size="sm"
										c="rgba(255, 255, 255, 0.9)"
										style={{ lineHeight: 1.6 }}
									>
										<Trans>
											Isi formulir pre-assessment untuk Medical Check Up AAA
										</Trans>
									</Text>
								</div>

								{/* Button */}
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
										padding: "12px 24px",
										borderRadius: "12px",
										background: "rgba(255, 255, 255, 0.2)",
										border: "1px solid rgba(255, 255, 255, 0.3)",
										backdropFilter: "blur(10px)",
										fontWeight: 500,
										fontSize: "14px",
									}}
								>
									<Trans>Mulai Sekarang</Trans>
									<span style={{ fontSize: "16px" }}>→</span>
								</div>
							</div>
						</div>
					</Grid.Col>
				</Grid>
			</Container>

			{/* QR Code Enlarged Modal */}
			<Modal
				opened={qrModalOpened}
				onClose={() => setQrModalOpened(false)}
				centered
				size="md"
				radius="lg"
				title={
					<Text fw={600} size="lg">
						<Trans>QR Code Booking</Trans>
					</Text>
				}
			>
				{currentBooking && (
					<Stack align="center" gap="lg" py="md">
						<div
							style={{
								padding: "2rem",
								background: "white",
								borderRadius: "20px",
								border: "2px solid #e5e7eb",
								boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
							}}
						>
							<Image
								src={`https://quickchart.io/qr?text=${currentBooking.booking_kode}&size=400`}
								alt="QR Code"
								w={300}
								h={300}
							/>
						</div>
						<div style={{ textAlign: "center" }}>
							<Text size="sm" c="gray.6" mb={4}>
								<Trans>Kode Booking</Trans>
							</Text>
							<Text size="xl" fw={700} style={{ letterSpacing: "1px" }}>
								{currentBooking.booking_kode}
							</Text>
						</div>
					</Stack>
				)}
			</Modal>

			{/* MCU Coming Soon Modal */}
			<Modal
				opened={mcuModalOpened}
				onClose={() => setMcuModalOpened(false)}
				centered
				size="md"
				radius="lg"
				withCloseButton={false}
			>
				<Stack align="center" gap="xl" py="xl" px="md">
					{/* Icon */}
					<div
						style={{
							width: "100px",
							height: "100px",
							borderRadius: "50%",
							background: "linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							boxShadow: "0 10px 30px rgba(59, 130, 246, 0.3)",
						}}
					>
						<Stethoscope size={48} color="white" strokeWidth={2} />
					</div>

					{/* Title */}
					<Title order={2} ta="center" c="gray.8">
						<Trans>Coming Soon</Trans>
					</Title>

					{/* Description */}
					<Text size="md" ta="center" c="gray.6" px="md">
						<Trans>
							Fitur MCU Pre-Assessment sedang dalam tahap pengembangan dan akan
							segera hadir untuk Anda.
						</Trans>
					</Text>

					{/* Button */}
					<button
						type="button"
						onClick={() => setMcuModalOpened(false)}
						style={{
							width: "100%",
							maxWidth: "200px",
							padding: "12px 24px",
							background: "linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)",
							color: "white",
							border: "none",
							borderRadius: "12px",
							fontSize: "15px",
							fontWeight: 600,
							cursor: "pointer",
							boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
							transition: "transform 0.2s",
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.transform = "translateY(-2px)";
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.transform = "translateY(0)";
						}}
					>
						<Trans>Mengerti</Trans>
					</button>
				</Stack>
			</Modal>
		</div>
	);
}
